import { RouteObject, useLocation, useNavigationType, useParams, useRoutes, useSearchParams } from "react-router-dom";
import { useNavigate } from 'src/hooks/useNavigate';
import React, { Suspense, lazy, useContext, useEffect } from "react";
import { KeepAlive, useAliveController } from "react-activation";
import { Context } from "src/stores/context";
import { getNeedIPhoneHomeIndicatorAdaptation, i18nChangeLanguage } from "src/utils";
import { getPreviousRoutePath, routeHistoryManager } from "src/utils/routeHistory";
import { smartKeepAliveManager } from "src/utils/smartKeepAliveManager";

import "./index.less";

// 懒加载页面组件
const EmailLogin = lazy(() => import("src/pages/EmailLogin"));
// 加载中组件
const LoginInfoImprove = lazy(() => import("../pages/LoginInfoImprove"));
const OAuth = lazy(() => import("../pages/OAuth"));
// const Language = lazy(() => import("../pages/Language"));
const FontSize = lazy(() => import("../pages/FontSize"));

const ImagePreview = lazy(() => import("../pages/ImagePreview"));

const Home = lazy(() => import("src/pages/Home"))
const Homepage = lazy(() => import("src/pages/Homepage"));

const CreateHomepage = lazy(() => import("src/pages/CreateHomepage"));
const EditHomepage = lazy(() => import("src/pages/EditHomepage"));

const ActDetail = lazy(() => import("src/pages/ActDetail"));
const OrderPay = lazy(() => import("src/pages/OrderPay"));
const OrderSuccess = lazy(() => import("src/pages/OrderSuccess"));
const AddressEdit = lazy(() => import("src/pages/AddressEdit"));
const AddressList = lazy(() => import("src/pages/AddressList"));
const SupplierActList = lazy(() => import("src/pages/SupplierActList"));
const SupplierProductList = lazy(() => import("src/pages/SupplierProductList"));
const CustomerOrderList = lazy(() => import("src/pages/CustomerOrderList"));
const CustomerOrderDetail = lazy(() => import("src/pages/CustomerOrderDetail"));
const BusinessOrderDetail = lazy(() => import("src/pages/BusinessOrderDetail"));
const CustomerEvaluationList = lazy(() => import("src/pages/CustomerEvaluationList"));
const EvaluationManagement = lazy(() => import("src/pages/EvaluationManagement"));
const LogisticsDetail = lazy(() => import("src/pages/LogisticsDetail"));
const BusinessOrderList = lazy(() => import("src/pages/BusinessOrderList"));
const PendingCommissionList = lazy(() => import("src/pages/PendingCommissionList"));
const PendingCommissionListDetail = lazy(() => import("src/pages/PendingCommissionListDetail"));
const AccountBalance = lazy(() => import("src/pages/AccountBalance"));
const FundDetail = lazy(() => import("src/pages/FundDetail"));
const WithdrawRecord = lazy(() => import("src/pages/WithdrawRecord"));
const Withdraw = lazy(() => import("src/pages/Withdraw"));
const WithdrawDetail = lazy(() => import("src/pages/WithdrawDetail"));
const AddBankCard = lazy(() => import("src/pages/AddBankCard"));
const BankCardSelect = lazy(() => import("src/pages/BankCardSelect"));
const FansList = lazy(() => import("src/pages/FansList"));

const PrivacyPolicy = lazy(() => import("src/pages/PrivacyPolicy"));
const UserAgreement = lazy(() => import("src/pages/UserAgreement"));
const FollowedSellers = lazy(() => import("src/pages/FollowedSellers"));
const BrowseHistory = lazy(() => import("src/pages/BrowseHistory"));
const BusinessBrowseHistory = lazy(() => import("src/pages/BusinessBrowseHistory"));
const PublishAct = lazy(() => import("src/pages/PublishAct"));
const OrderComment = lazy(() => import("src/pages/OrderComment"));
const RefundApplication = lazy(() => import("src/pages/RefundApplication"));


// 加载中组件
const PageLoading = () => (
  <div className="height-full flexMainXYcenter flexMainYYcenter">
    <div className="main-loading"></div>
  </div>
);

// 懒加载包装组件
const LazyLoadWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoading />}>
    {children}
  </Suspense>
);

// 预加载管理器 - 用于优化页面切换体验
export const preloadManager = {
/*  // 预加载首页相关页面
  preloadHome: () => {
    import(/!* webpackChunkName: "meeting" *!/ "src/pages/MeetingV3");
    import(/!* webpackChunkName: "translator" *!/ "src/pages/TranslatorV3")
  },*/
  
  // 根据当前页面智能预加载可能访问的页面
  smartPreload: (currentPath: string) => {
/*    if (currentPath === '/') {
      setTimeout(() => preloadManager.preloadHome(), 2000);
    }*/
  }
};

// Homepage包装组件，让不同ghId使用不同的缓存实例
// const HomepageWrapper = () => {
//   const location = useLocation();
//   const searchParams = new URLSearchParams(location.search);
//   const ghId = Number(searchParams.get('ghId')) || undefined;
//   // 动态生成cacheKey，确保不同ghId使用不同的缓存实例
//   const cacheKey = ghId ? `homepage_${ghId}` : 'homepage_default';
//   return (
//     <ConditionalKeepAlive name="homepage" cacheKey={cacheKey}>
//       <Suspense fallback={<PageLoading />}>
//         <Homepage/>
//       </Suspense>
//     </ConditionalKeepAlive>
//   );
// };

// 提现缓存
const WithdrawWrapper = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const ghId = searchParams.get('ghId') || '';
  // 动态生成cacheKey，确保不同ghId使用不同的缓存实例
  const cacheKey = ghId ? `withdraw_${ghId}` : 'withdraw_default';
  return (
    <ConditionalKeepAlive name="withdraw" cacheKey={cacheKey}>
      <Suspense fallback={<PageLoading />}>
        <Withdraw />
      </Suspense>
    </ConditionalKeepAlive>
  );
};

// 活动详情缓存
const ActDetailWrapper = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const actId = searchParams.get('actId') || '';
  // 动态生成cacheKey，确保不同actId使用不同的缓存实例
  const cacheKey = actId ? `actDetail_${actId}` : 'actDetail_default';
  return (
    <ConditionalKeepAlive name="actDetail" cacheKey={cacheKey}>
      <Suspense fallback={<PageLoading />}>
        <ActDetail />
      </Suspense>
    </ConditionalKeepAlive>
  );
};

// 选择银行卡缓存
const BankCardSelectWrapper = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const ghId = searchParams.get('ghId') || '';
  // 动态生成cacheKey，确保不同ghId使用不同的缓存实例
  const cacheKey = ghId ? `bankCardSelect_${ghId}` : 'bankCardSelect_default';
  return (
    <ConditionalKeepAlive name="bankCardSelect" cacheKey={cacheKey}>
      <Suspense fallback={<PageLoading />}>
        <BankCardSelect />
      </Suspense>
    </ConditionalKeepAlive>
  );
};

// 下单缓存
const OrderPayWrapper = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const actId = searchParams.get('actId') || '';
  const goodsKey = searchParams.get('goodsKey') || '';
  // 动态生成cacheKey，确保不同actId使用不同的缓存实例
  const cacheKey = actId ? `orderPay_${actId}_${goodsKey}` : 'orderPay_default';
  return (
    <ConditionalKeepAlive name="orderPay" cacheKey={cacheKey}>
      <Suspense fallback={<PageLoading />}>
        <OrderPay />
      </Suspense>
    </ConditionalKeepAlive>
  );
};

/**
 * 使用ConditionalKeepAlive注意事项：页面中的最外层div高度超100%出现滚动条后，如果不设置高100%及y轴可滚动，在ios会出现数据是缓存的，但是滚动条位置不对的bug
 * min-height: 100%;错误
 * 按以下设置
 * height: 100%;
 * overflow-y: auto;
 * 设置了缓存后，push进入页面没有刷新还是上一次的状态，则在navigate前需要调用await controller.drop(name)
 * @param
 * @returns
 * 智能缓存管理
 * @description 需要在 SmartKeepAliveManager 中配置页面的 pageWeights 和 pagePriorities
 */
const ConditionalKeepAlive = ({ children, name, cacheKey }: {
  children: React.ReactNode,
  name: string,
  cacheKey: string
}) => {
  const controller = useAliveController();
  const navigationType = useNavigationType();
  // 获取当前最新的 Context 值
  const contextValue = useContext(Context);
  
  // 初始化智能管理器
  useEffect(() => {
    if (controller && !smartKeepAliveManager['aliveController']) {
      smartKeepAliveManager.setAliveController(controller);
    }
  }, [controller]);
  
  // 页面访问追踪和智能缓存管理
  useEffect(() => {
    const isBackNavigation = navigationType === "POP";
    // 记录页面访问 - 只在页面真正切换时调用一次
    smartKeepAliveManager.onPageAccess(name, cacheKey);
    // 刷新缓存逻辑
    if (!isBackNavigation) {
      controller.refresh(name);
    }
    // 清理相同页面 - 不同 cacheKey 的缓存
    const dropList = controller.getCachingNodes().filter((item) => item.name === name && item.cacheKey !== cacheKey).map(({ id }) => id);
    if (dropList.length) { controller.dropScopeByIds(dropList); }
  }, [name]); // 只依赖name，确保只在真正切换页面时触发一次

  // return <KeepAlive name={name} cacheKey={cacheKey}>{children}</KeepAlive>;
  return  <KeepAlive name={name} cacheKey={cacheKey}>
            {/* 为缓存的组件提供最新的 Context 值 */}
            <Context.Provider value={contextValue}>
              {children}
            </Context.Provider>
          </KeepAlive>
};

export const routes: RouteObject[] = [
  // {
  //   path: "/",
  //   element: <LazyLoadWrapper>
  //     <Home />
  //   </LazyLoadWrapper>,
  // },
  {
    path: "/",
    element: <ConditionalKeepAlive name="home" cacheKey="home">
      <LazyLoadWrapper>
        <Home />
      </LazyLoadWrapper>
    </ConditionalKeepAlive>,
  },
  {
    path: "/homepage",
    element: (
      <LazyLoadWrapper>
        {/* <HomepageWrapper/> */}
        <Homepage/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/createHomepage",
    element: (
      <LazyLoadWrapper>
        <CreateHomepage/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/editHomepage",
    element: (
      <LazyLoadWrapper>
        <EditHomepage/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/emailLogin",
    element: (
      <LazyLoadWrapper>
        <EmailLogin/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/oauth",
    element: (
      <LazyLoadWrapper>
        <OAuth/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/loginInfoImprove",
    element: (
      <LazyLoadWrapper>
        <LoginInfoImprove/>
      </LazyLoadWrapper>
    ),
  },
  // {
  //   path: "/language",
  //   element: (
  //     <LazyLoadWrapper>
  //       <Language/>
  //     </LazyLoadWrapper>
  //   ),
  // },
  {
    path: "/font-size",
    element: (
      <LazyLoadWrapper>
        <FontSize/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/imagePreview",
    element: (
      <LazyLoadWrapper>
        <ImagePreview />
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/actDetail",
    element: <ActDetailWrapper/>,
    // element: (
    //   <LazyLoadWrapper>
    //     <ActDetail/>
    //   </LazyLoadWrapper>
    // ),
  },
  {
    path: "/orderPay",
    // element: (
    //   <LazyLoadWrapper>
    //     <OrderPay/>
    //   </LazyLoadWrapper>
    // ),
    element: <OrderPayWrapper/>,
  },
  {
    path: "/orderSuccess",
    element: (
      <LazyLoadWrapper>
        <OrderSuccess/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/addressEdit",
    element: (
      <LazyLoadWrapper>
        <AddressEdit/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/addressList",
    element: (
      <LazyLoadWrapper>
        <AddressList/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/supplierActList",
    element: (
      <LazyLoadWrapper>
        <SupplierActList/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/supplierProductList",
    element: (
      <LazyLoadWrapper>
        <SupplierProductList/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/customerOrderList",
    // element: (
    //   <LazyLoadWrapper>
    //     <CustomerOrderList/>
    //   </LazyLoadWrapper>
    // ),
    element: <ConditionalKeepAlive name="customerOrderList" cacheKey="customerOrderList">
      <LazyLoadWrapper>
        <CustomerOrderList />
      </LazyLoadWrapper>
    </ConditionalKeepAlive>,
  },
  {
    path: "/customerOrderDetail",
    element: (
      <LazyLoadWrapper>
        <CustomerOrderDetail/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/businessOrderDetail",
    element: (
      <LazyLoadWrapper>
        <BusinessOrderDetail/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/customerEvaluationList",
    element: (
      <LazyLoadWrapper>
        <CustomerEvaluationList/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/evaluationManagement",
    element: (
      <LazyLoadWrapper>
        <EvaluationManagement/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/logisticsDetail",
    element: (
      <LazyLoadWrapper>
        <LogisticsDetail/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/businessOrderList",
    element: (
      <LazyLoadWrapper>
        <BusinessOrderList/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/pendingCommissionList",
    element: (
      <LazyLoadWrapper>
        <PendingCommissionList/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/pendingCommissionListDetail",
    element: (
      <LazyLoadWrapper>
        <PendingCommissionListDetail/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/fansList",
    element: (
      <LazyLoadWrapper>
        <FansList/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/accountBalance",
    element: (
      <LazyLoadWrapper>
        <AccountBalance/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/fundDetail",
    element: (
      <LazyLoadWrapper>
        <FundDetail/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/withdrawRecord",
    element: (
      <LazyLoadWrapper>
        <WithdrawRecord/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/withdrawDetail",
    element: (
      <LazyLoadWrapper>
        <WithdrawDetail/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/withdraw",
    element: <WithdrawWrapper/>,
  },
  {
    path: "/addBankCard",
    element: (
      <LazyLoadWrapper>
        <AddBankCard/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/bankCardSelect",
    element: <BankCardSelectWrapper/>,
  },
  {
    path: "/privacyPolicy",
    element: (
      <LazyLoadWrapper>
        <PrivacyPolicy/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/userAgreement",
    element: (
      <LazyLoadWrapper>
        <UserAgreement/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/followedSellers",
    element: (
      <LazyLoadWrapper>
        <FollowedSellers/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/browseHistory",
    element: (
      <LazyLoadWrapper>
        <BrowseHistory/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/businessBrowseHistory",
    element: (
      <LazyLoadWrapper>
        <BusinessBrowseHistory/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/publishAct",
    element: (
      <LazyLoadWrapper>
        <PublishAct/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/orderComment",
    element: (
      <LazyLoadWrapper>
        <OrderComment/>
      </LazyLoadWrapper>
    ),
  },
  {
    path: "/refundApplication",
    element: (
      <LazyLoadWrapper>
        <RefundApplication/>
      </LazyLoadWrapper>
    ),
  },
];

// 下载页面重定向组件
// function DownloadRedirect() {
//   const location = useLocation();
  
//   useEffect(() => {
//     if (location.pathname === "/download") {
//       window.location.href = "https://www.blablalink.live";
//     }
//   }, [location.pathname]);

//   return null;
// }

// 统一路由参数处理组件
function RouteParamsHandler() {
  
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  useEffect(() => {
    // 处理 setToken
    const token = searchParams.get("setToken");
    const uid = searchParams.get("setUid");
    const languageCode = searchParams.get("setLanguageCode");
    
    if (token && uid && languageCode) {
      localStorage.setItem("token", token);
      localStorage.setItem("uid", uid);
      localStorage.setItem("languageCode", languageCode);
      i18nChangeLanguage(languageCode);
      searchParams.delete("setToken");
      searchParams.delete("setUid");
      searchParams.delete("setLanguageCode");
      navigate({ search: searchParams.toString() }, { replace: true });
      navigate({ search: searchParams.toString() }, { replace: true });
    }
  }, [searchParams]);
  
  return null;
}

// 路由历史拦截器组件
function RouteHistoryInterceptor() {
  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    // 记录路由变化
    const navigationTypeMapping: { [key: string]: 'PUSH' | 'REPLACE' | 'POP' } = {
      'PUSH': 'PUSH',
      'REPLACE': 'REPLACE',
      'POP': 'POP'
    };

    const mappedNavigationType = navigationTypeMapping[navigationType] || 'PUSH';
    
    // 创建路由历史项
    const routeHistoryItem = {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash
    };
    
    // 添加路由记录
    routeHistoryManager.addRoute(
      routeHistoryItem,
      mappedNavigationType
    );
    
    // 智能预加载 - 根据当前页面预加载可能访问的页面
    preloadManager.smartPreload(location.pathname);

    // 可选：在开发环境下打印路由变化信息
    if (process.env.NODE_ENV === 'development') {
      console.log('路由变化记录:', {
        path: location.pathname,
        fullPath: `${location.pathname}${location.search}${location.hash}`,
        navigationType: mappedNavigationType,
        timestamp: new Date().toISOString()
      });
    }
  }, [location.pathname, location.search, location.hash, navigationType]);

  return null;
}

export const AppRoutes = () => {
  const needAdaptation = getNeedIPhoneHomeIndicatorAdaptation()
  return (
    <>
      {/* 插入统一路由参数处理器 */}
      <RouteParamsHandler />
      {/* 插入路由历史拦截器 */}
      <RouteHistoryInterceptor />
      {/* 插入短链拦截器 */}
      {/* <ShortLinkInterceptor /> */}
      {/* 下载页面重定向 */}
      {/* <DownloadRedirect /> */}
      {/* 原有路由配置 */}
      <div className={`height-full ${needAdaptation ? "flutter-safe-container" : ""}`}>{useRoutes(routes)}</div>
    </>
  )
};